import React, { useMemo, useCallback, useEffect, useState, useRef } from "react";
import { useAppManager } from "../../hooks/useAppManager";
import NoTenantPlaceholder from "./NoTenantPlaceholder";
import KeysCard from "./KeysCard";
import KeyDetailCard from "./KeyDetailCard";

const CONTENT_HEIGHT_CALC = "calc(100vh - 160px)";

function AppManager() {
  const {
    selectedTenant,
    selectedKey,
    selectedValue,
    fetchKeys,
    getQrCodeImage,
  } =
    useAppManager();

  const selectedTenantName = selectedTenant?.name ?? null;

  const qrIdentifier = selectedValue?.rina_id || selectedKey;

  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const qrCodeUrlRef = useRef(null);

  useEffect(() => {
    let isCancelled = false;

    const loadQrCode = async () => {
      if (!qrIdentifier || !selectedTenantName) {
        setQrCodeUrl((previousUrl) => {
          if (previousUrl?.startsWith("blob:")) {
            window.URL.revokeObjectURL(previousUrl);
          }
          qrCodeUrlRef.current = null;
          return null;
        });
        return;
      }

      try {
        const nextUrl = await getQrCodeImage(qrIdentifier, selectedTenantName);
        if (isCancelled) {
          if (nextUrl?.startsWith("blob:")) {
            window.URL.revokeObjectURL(nextUrl);
          }
          return;
        }

        setQrCodeUrl((previousUrl) => {
          if (previousUrl?.startsWith("blob:")) {
            window.URL.revokeObjectURL(previousUrl);
          }
          qrCodeUrlRef.current = nextUrl;
          return nextUrl;
        });
      } catch (error) {
        if (process.env.NODE_ENV === "development") {
          console.error("Error loading QR code:", error);
        }
        if (!isCancelled) {
          setQrCodeUrl((previousUrl) => {
            if (previousUrl?.startsWith("blob:")) {
              window.URL.revokeObjectURL(previousUrl);
            }
            qrCodeUrlRef.current = null;
            return null;
          });
        }
      }
    };

    loadQrCode();

    return () => {
      isCancelled = true;
    };
  }, [qrIdentifier, selectedTenantName, getQrCodeImage]);

  useEffect(
    () => () => {
      if (qrCodeUrlRef.current?.startsWith("blob:")) {
        window.URL.revokeObjectURL(qrCodeUrlRef.current);
      }
    },
    []
  );

  const handleRefreshKeys = useCallback(() => {
    fetchKeys(true);
  }, [fetchKeys]);

  const keysCardProps = useMemo(
    () => ({ handleRefreshKeys }),
    [handleRefreshKeys]
  );

  const keyDetailProps = useMemo(() => ({ qrCodeUrl }), [qrCodeUrl]);

  // layout
  return (
    <div className="w-full h-full flex flex-col">
      <div
        className="flex flex-col md:flex-row gap-4 flex-1"
        style={{ height: CONTENT_HEIGHT_CALC }}
      >
        {!selectedTenant && <NoTenantPlaceholder />}

        {selectedTenant && (
          <>
            <KeysCard {...keysCardProps} />
            <KeyDetailCard {...keyDetailProps} />
          </>
        )}
      </div>
    </div>
  );
}

export default React.memo(AppManager);
